/**
 * Single source of truth for "which paths are reachable without a login".
 *
 * Three independent guards historically each kept their own copy of this
 * list — `src/proxy.ts` (Edge), `src/components/app-shell.tsx`
 * (client shell), and `src/components/auth-provider.tsx` (client redirect).
 * That meant a path could be marked public in two of three places and a
 * user would still be bounced to /login by the third (which is exactly
 * how /docs broke after we shipped the docs site). Keep them aligned.
 *
 * Edge-compatible: this module exports strings only, no runtime deps.
 */

/**
 * Paths that match exactly (no prefix). `/` lives here so prefix-matching
 * `/` doesn't accidentally make every route public.
 */
export const PUBLIC_EXACT: readonly string[] = [
  "/",
  // Marketing category hubs. EXACT so /integrations is public without making
  // /integrations a public *prefix* (that would also expose …/link OAuth flows).
  "/use-cases",
  "/integrations",
  // Integration marketing pages. EXACT (not prefix) on purpose: the OAuth
  // link-confirm flows under /integrations/<provider>/link must keep requiring
  // a login + app shell, so we must not make /integrations/<provider> a public
  // *prefix* (that would also match …/link).
  "/integrations/slack",
  "/integrations/teams",
  "/integrations/jira",
  "/integrations/freshdesk",
  "/integrations/odoo",
  "/integrations/topdesk",
  "/integrations/afas",
  "/integrations/youforce",
  "/integrations/github",
  "/integrations/gitlab",
  "/integrations/argo",
  "/integrations/fc-agent",
];

/**
 * Paths whose prefix marks them public. `pathname.startsWith(p)` is
 * the test, so include any trailing `/` deliberately.
 */
export const PUBLIC_PREFIXES: readonly string[] = [
  "/login",
  "/signup",
  // Self-service password reset — a locked-out user reaches these WITHOUT a
  // session (the emailed token is the auth on the confirm step). No private
  // sub-routes live under either, so a prefix is safe.
  "/forgot-password",
  "/reset-password",
  // Public marketing + legal pages. Reachable without a login, and — crucially
  // — rendered WITHOUT the authenticated app shell even when a logged-in user
  // visits them (the shell keys off this same predicate). Without these, a
  // signed-in visitor sees the marketing/legal page wrapped in the dashboard
  // sidebar + topbar, and a signed-out visitor gets bounced to /login.
  "/about",
  "/contact",
  "/demo",
  "/pricing",
  // Business-value marketing page (value blocks + ROI calculator). No private
  // sub-routes live under /business-value, so a prefix is safe.
  "/business-value",
  // Product overview page. No private sub-routes live under /product, so a
  // prefix is safe.
  "/product",
  // Security marketing page (the nav's Security mega-menu destination). No
  // private sub-routes live under /security, so a prefix is safe.
  "/security",
  // Use-case marketing landing pages. No private sub-routes live under
  // /use-cases, so a prefix is safe and future-proof.
  "/use-cases/",
  // Comparison marketing pages (/compare/workflow-automation etc.). No
  // private sub-routes live under /compare, so a prefix is safe.
  "/compare/",
  "/privacy",
  "/terms",
  "/intake-demo",
  "/intake/",
  "/docs",
  // Portable governance approvals: an external approver opens this confirm page
  // from an email link without a FixControl login. The token is the auth.
  "/governance/approve",
  // Same for an inbox reply waiting for approval (comm approvals): the
  // single-use communication token is the auth; the click re-authorizes the
  // user it was minted for.
  "/comm/approve",
  // Read-only share view: a stakeholder opens an issue dossier (issue + patch +
  // mission timeline) from a Slack link without a FixControl login. The signed,
  // per-tenant, view-only share token is the auth (see share-token.ts). No
  // actions are ever rendered here.
  "/v/",
];

/** Same predicate every guard uses. */
export function isPublicPath(pathname: string): boolean {
  if (PUBLIC_EXACT.includes(pathname)) return true;
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}
